import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { cn } from './cn';

export type IconButtonVariant = 'default' | 'ghost' | 'primary';
export type IconButtonSize = 'sm' | 'md' | 'lg';

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  /** Required for a11y when the button has no visible label. */
  ariaLabel: string;
  icon: ReactNode;
  hasDot?: boolean;
}

// Also constrain the glyph — the icon SVGs carry no intrinsic width/height, so
// without this they render oversized. `[&>svg]` sizes the direct-child icon.
const sizeClasses: Record<IconButtonSize, string> = {
  sm: 'h-8 w-8 [&>svg]:h-4 [&>svg]:w-4',
  md: 'h-10 w-10 [&>svg]:h-5 [&>svg]:w-5',
  lg: 'h-12 w-12 [&>svg]:h-6 [&>svg]:w-6',
};

const variantClasses: Record<IconButtonVariant, string> = {
  default: 'bg-surface text-foreground border border-border hover:bg-bg',
  ghost: 'bg-transparent text-foreground hover:bg-surface',
  primary: 'bg-primary text-primary-foreground hover:opacity-90',
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    variant = 'default',
    size = 'md',
    ariaLabel,
    icon,
    hasDot = false,
    className,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={ariaLabel}
      className={cn(
        'relative inline-flex items-center justify-center rounded-full transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        sizeClasses[size],
        variantClasses[variant],
        className,
      )}
      {...rest}
    >
      {icon}
      {hasDot && (
        <span
          aria-hidden="true"
          className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-danger ring-2 ring-bg"
        />
      )}
    </button>
  );
});
